// Bannière d'essai + quota affichée en haut de l'app (Lot 8.3 & 8.4).

import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import type { TenantPlanInfo } from "../api/types";
import { selectPlanInfo, useAuthStore } from "../stores/auth";

export function shouldShowTrialBanner(info: TenantPlanInfo | null): boolean {
  if (info === null) return false;
  if (info.trial_expired) return true;
  if (info.invoice_quota && info.invoice_quota.remaining === 0) return true;
  return info.plan?.slug === "trial" && info.trial_days_left !== null;
}

type Tone = "info" | "warning" | "danger";

function tone(info: TenantPlanInfo): Tone {
  if (info.trial_expired) return "danger";
  if (info.invoice_quota && info.invoice_quota.remaining === 0) return "danger";
  if ((info.trial_days_left ?? 0) <= 3) return "warning";
  return "info";
}

const TONE_STYLES: Record<Tone, { bg: string; text: string; border: string; cta: string }> = {
  info: {
    bg: "bg-brand-500/10",
    text: "text-brand-700",
    border: "border-brand-500/20",
    cta: "bg-brand-600 text-white hover:bg-brand-700",
  },
  warning: {
    bg: "bg-amber-50",
    text: "text-amber-800",
    border: "border-amber-200",
    cta: "bg-amber-600 text-white hover:bg-amber-700",
  },
  danger: {
    bg: "bg-red-50",
    text: "text-red-800",
    border: "border-red-200",
    cta: "bg-red-600 text-white hover:bg-red-700",
  },
};

export function TrialBanner() {
  const info = useAuthStore(selectPlanInfo);
  const { t } = useTranslation();

  if (!shouldShowTrialBanner(info)) return null;
  const planInfo = info as TenantPlanInfo;
  const styles = TONE_STYLES[tone(planInfo)];

  function trialMessage(p: TenantPlanInfo): string {
    if (p.trial_expired) return t("trialBanner.expired");
    if (p.invoice_quota && p.invoice_quota.remaining === 0) {
      const planName = p.plan?.name ?? "";
      return `${planName} (${p.invoice_quota.used} / ${p.invoice_quota.limit})`;
    }
    const days = p.trial_days_left ?? 0;
    if (days <= 0) return `${t("trialBanner.trialEnds")} aujourd'hui.`;
    return `${t("trialBanner.trialEnds")} J-${days}.`;
  }

  function quotaCounter(p: TenantPlanInfo): string | null {
    if (p.trial_expired) return null;
    if (p.invoice_quota === null) return null;
    if (p.invoice_quota.remaining === 0) return null;
    return `${p.invoice_quota.used} / ${p.invoice_quota.limit} ${t("common.invoices")}`;
  }

  const counter = quotaCounter(planInfo);
  const isBlocking =
    planInfo.trial_expired ||
    (planInfo.invoice_quota !== null && planInfo.invoice_quota.remaining === 0);

  return (
    <div
      role="status"
      aria-live="polite"
      className={[
        "border-b px-6 py-2 flex items-center justify-center gap-4 text-sm",
        styles.bg,
        styles.text,
        styles.border,
      ].join(" ")}
    >
      <span>{trialMessage(planInfo)}</span>
      {counter && (
        <span data-testid="trial-banner-counter" className="hidden sm:inline opacity-80">
          · {counter}
        </span>
      )}
      <Link
        to="/pricing"
        className={["rounded-md px-3 py-1 text-xs font-semibold transition-colors", styles.cta].join(" ")}
      >
        {isBlocking ? t("trialBanner.subscribe") : t("trialBanner.subscribe")}
      </Link>
    </div>
  );
}
